// Dev-only map-editing panel: freely place/remove any node and edit the
// resource budgets directly, bypassing cost/build-delay entirely. Gated
// behind an explicit toggle and, once a place/remove tool is armed, an
// unmissable "DEBUG:" banner in the HUD help text (see main.ts's loop) —
// this intercepts canvas clicks ahead of every normal build mode, so it
// must never be silently on.

import { CONSUMER_TYPES, CONSUMER_TYPE_IDS, PRODUCER_TYPES, PRODUCER_TYPE_IDS, STORAGE_TYPES } from "./config";
import type { DebugPlaceSpec } from "./simulation";
import type { GameState } from "./state";

export interface DebugCallbacks {
  onSetLineBudget: (km: number) => void;
  onSetControlNodeBudget: (n: number) => void;
  onSetBatteryBudget: (n: number) => void;
}

export type DebugTool = { action: "place"; spec: DebugPlaceSpec } | { action: "remove" } | null;

const panelEl = document.getElementById("debug-panel") as HTMLDivElement;
const toolsEl = document.getElementById("debug-tools") as HTMLDivElement;
const toggleBtn = document.getElementById("btn-debug-toggle") as HTMLButtonElement;
const closeBtn = document.getElementById("debug-close") as HTMLButtonElement;
const lineBudgetInput = document.getElementById("debug-line-budget") as HTMLInputElement;
const controlBudgetInput = document.getElementById("debug-control-budget") as HTMLInputElement;
const batteryBudgetInput = document.getElementById("debug-battery-budget") as HTMLInputElement;

let isOpen = false;
let armedTool: DebugTool = null;
const toolButtons: { el: HTMLButtonElement; tool: DebugTool }[] = [];

function toolsEqual(a: DebugTool, b: DebugTool): boolean {
  if (a === null || b === null) return a === b;
  if (a.action !== b.action) return false;
  if (a.action === "remove") return true;
  return JSON.stringify(a.spec) === JSON.stringify((b as { action: "place"; spec: DebugPlaceSpec }).spec);
}

function updateToolButtonStyles(): void {
  for (const { el, tool } of toolButtons) el.classList.toggle("selected", toolsEqual(tool, armedTool));
}

function setArmedTool(tool: DebugTool): void {
  // Clicking the already-armed tool disarms it — same toggle convention as
  // the inspector's control-node chip selection.
  armedTool = toolsEqual(armedTool, tool) ? null : tool;
  updateToolButtonStyles();
}

function buildToolButton(label: string, tool: DebugTool, extraClass?: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = extraClass ? `mode-btn debug-tool-btn ${extraClass}` : "mode-btn debug-tool-btn";
  btn.textContent = label;
  btn.addEventListener("click", () => setArmedTool(tool));
  toolButtons.push({ el: btn, tool });
  return btn;
}

function buildTools(): void {
  toolsEl.innerHTML = "";

  const producerGroup = document.createElement("div");
  producerGroup.className = "debug-tool-group";
  for (const typeId of PRODUCER_TYPE_IDS) {
    producerGroup.appendChild(buildToolButton(PRODUCER_TYPES[typeId].label, { action: "place", spec: { kind: "producer", typeId } }));
  }
  toolsEl.appendChild(producerGroup);

  const consumerGroup = document.createElement("div");
  consumerGroup.className = "debug-tool-group";
  for (const typeId of CONSUMER_TYPE_IDS) {
    consumerGroup.appendChild(buildToolButton(CONSUMER_TYPES[typeId].label, { action: "place", spec: { kind: "consumer", typeId } }));
  }
  toolsEl.appendChild(consumerGroup);

  const otherGroup = document.createElement("div");
  otherGroup.className = "debug-tool-group";
  otherGroup.appendChild(buildToolButton(STORAGE_TYPES.dam.label, { action: "place", spec: { kind: "storage", source: "dam" } }));
  otherGroup.appendChild(buildToolButton(STORAGE_TYPES.battery.label, { action: "place", spec: { kind: "storage", source: "battery" } }));
  otherGroup.appendChild(buildToolButton("Control Node", { action: "place", spec: { kind: "control" } }));
  toolsEl.appendChild(otherGroup);

  const removeGroup = document.createElement("div");
  removeGroup.className = "debug-tool-group";
  removeGroup.appendChild(buildToolButton("🗑 Remove", { action: "remove" }, "debug-remove-btn"));
  toolsEl.appendChild(removeGroup);
}

export function isDebugPanelOpen(): boolean {
  return isOpen;
}

export function getArmedDebugTool(): DebugTool {
  return armedTool;
}

export function closeDebugPanel(): void {
  isOpen = false;
  armedTool = null;
  panelEl.classList.add("hidden");
  toggleBtn.classList.remove("debug-active");
  updateToolButtonStyles();
}

export function toggleDebugPanel(onOpen?: () => void): void {
  isOpen = !isOpen;
  panelEl.classList.toggle("hidden", !isOpen);
  toggleBtn.classList.toggle("debug-active", isOpen);
  if (!isOpen) {
    armedTool = null;
    updateToolButtonStyles();
  } else {
    onOpen?.();
  }
}

/** Disarms the current tool without closing the panel — used by Escape. */
export function disarmDebugTool(): void {
  armedTool = null;
  updateToolButtonStyles();
}

export function debugToolLabel(tool: DebugTool): string {
  if (!tool) return "";
  if (tool.action === "remove") return "DEBUG: click any node to remove it instantly";
  const spec = tool.spec;
  const label =
    spec.kind === "producer"
      ? PRODUCER_TYPES[spec.typeId]?.label
      : spec.kind === "consumer"
        ? CONSUMER_TYPES[spec.typeId]?.label
        : spec.kind === "storage"
          ? STORAGE_TYPES[spec.source].label
          : "Control Node";
  return `DEBUG: click a hex to place a ${label ?? spec.kind}`;
}

export function initDebugPanel(callbacks: DebugCallbacks): void {
  buildTools();
  closeBtn.addEventListener("click", () => closeDebugPanel());

  lineBudgetInput.addEventListener("input", () => {
    const v = Number(lineBudgetInput.value);
    if (Number.isFinite(v)) callbacks.onSetLineBudget(v);
  });
  controlBudgetInput.addEventListener("input", () => {
    const v = Number(controlBudgetInput.value);
    if (Number.isFinite(v)) callbacks.onSetControlNodeBudget(v);
  });
  batteryBudgetInput.addEventListener("input", () => {
    const v = Number(batteryBudgetInput.value);
    if (Number.isFinite(v)) callbacks.onSetBatteryBudget(v);
  });
}

/** Cheap per-frame sync (matching the inspector's pattern) so the numbers
 * stay live if the panel is left open while the game clock advances (e.g.
 * budgets replenishing) — guarded so it never clobbers an in-progress edit. */
export function refreshDebugPanel(state: GameState): void {
  if (!isOpen) return;
  if (document.activeElement !== lineBudgetInput) lineBudgetInput.value = String(state.lineBudgetKm);
  if (document.activeElement !== controlBudgetInput) controlBudgetInput.value = String(state.controlNodeBudget);
  if (document.activeElement !== batteryBudgetInput) batteryBudgetInput.value = String(state.batteryBudget);
}
